import {
  Alert,
  AlertDescription,
  Avatar,
  AvatarFallback,
  Badge,
  Button,
  ColumnHint,
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  Input,
  Label,
  LoadingState,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
  initials,
  toast,
} from '@sitewise/ui';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Plus } from 'lucide-react';
import { useState } from 'react';


import { fetchMe } from '../../../api/auth.js';
import {
  deactivateOrgUser,
  inviteOrgUser,
  listOrgUsers,
  updateOrgUserRole,
  updateOrgUserTabletMode,
} from '../../../api/org.js';
import { ApiError } from '../../../lib/api-client.js';

const ROLES = ['admin', 'building_manager', 'maintenance', 'case_manager', 'auditor', 'vendor'];

const ROLE_LABELS: Record<string, string> = {
  admin: 'Administrator',
  building_manager: 'Building manager',
  maintenance: 'Maintenance staff',
  case_manager: 'Case manager',
  auditor: 'Auditor (read-only)',
  vendor: 'Vendor',
};

export function OrgUsersPage() {
  const queryClient = useQueryClient();
  const meQuery = useQuery({ queryKey: ['me'], queryFn: fetchMe });
  const usersQuery = useQuery({ queryKey: ['org-users'], queryFn: listOrgUsers });
  const [inviteOpen, setInviteOpen] = useState(false);

  const inviteMutation = useMutation({
    mutationFn: inviteOrgUser,
    onSuccess: () => {
      toast.success('Invitation sent');
      setInviteOpen(false);
      queryClient.invalidateQueries({ queryKey: ['org-users'] });
    },
  });

  const roleMutation = useMutation({
    mutationFn: ({ id, role }: { id: string; role: string }) => updateOrgUserRole(id, role),
    onSuccess: () => {
      toast.success('Role updated');
      queryClient.invalidateQueries({ queryKey: ['org-users'] });
    },
    onError: (err) => {
      toast.error(err instanceof ApiError ? err.message : 'Could not update role');
    },
  });

  const tabletMutation = useMutation({
    mutationFn: ({ id, tabletMode }: { id: string; tabletMode: boolean }) =>
      updateOrgUserTabletMode(id, tabletMode),
    onSuccess: () => {
      toast.success('Tablet mode updated');
      queryClient.invalidateQueries({ queryKey: ['org-users'] });
    },
    onError: (err) => {
      toast.error(err instanceof ApiError ? err.message : 'Could not update tablet mode');
    },
  });

  const deactivateMutation = useMutation({
    mutationFn: deactivateOrgUser,
    onSuccess: () => {
      toast.success('User deactivated');
      queryClient.invalidateQueries({ queryKey: ['org-users'] });
    },
    onError: (err) => {
      toast.error(err instanceof ApiError ? err.message : 'Could not deactivate user');
    },
  });

  return (
    <>
      <div className="mb-4 flex items-center justify-between">
        <div>
          <h2 className="text-lg font-semibold">Users</h2>
          <p className="text-sm text-fg-secondary">
            Invite teammates and manage roles. Enable tablet mode for a wall display that
            only shows work orders (view/open only — cannot close them).
          </p>
        </div>
        <Button onClick={() => setInviteOpen(true)}>
          <Plus className="h-4 w-4" />
          Invite user
        </Button>
      </div>

      {usersQuery.isPending ? (
        <LoadingState rows={5} />
      ) : usersQuery.isError ? (
        <Alert variant="danger">
          <AlertDescription>Could not load users.</AlertDescription>
        </Alert>
      ) : (
        <div className="overflow-hidden rounded-lg border border-border bg-surface">
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>
                  <ColumnHint description="The person's name in this organization.">Name</ColumnHint>
                </TableHead>
                <TableHead>
                  <ColumnHint description="The address they use to sign in.">Email</ColumnHint>
                </TableHead>
                <TableHead>
                  <ColumnHint description="What they are allowed to do. Administrators manage the organization.">
                    Role
                  </ColumnHint>
                </TableHead>
                <TableHead>
                  <ColumnHint description="When on, this person sees a simplified wall-display layout for work orders.">
                    Tablet mode
                  </ColumnHint>
                </TableHead>
                <TableHead className="text-right">
                  <ColumnHint description="Change this person's role, tablet mode, or access.">Actions</ColumnHint>
                </TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {usersQuery.data.users.map((user) => {
                const isMe = user.id === meQuery.data?.user.id;
                return (
                  <TableRow key={user.id}>
                    <TableCell>
                      <div className="flex items-center gap-2">
                        <Avatar className="h-7 w-7">
                          <AvatarFallback>{initials(user.name || user.email)}</AvatarFallback>
                        </Avatar>
                        <div>
                          <div className="font-medium">{user.name}</div>
                          {user.totpEnabled ? (
                            <div className="text-xs text-fg-muted">TOTP enabled</div>
                          ) : null}
                        </div>
                      </div>
                    </TableCell>
                    <TableCell>{user.email}</TableCell>
                    <TableCell>
                      {isMe ? (
                        <Badge variant="primary">{ROLE_LABELS[user.role] ?? user.role}</Badge>
                      ) : (
                        <select
                          className="h-8 rounded border border-border bg-surface px-2 text-sm"
                          value={user.role}
                          onChange={(e) =>
                            roleMutation.mutate({ id: user.id, role: e.target.value })
                          }
                        >
                          {ROLES.map((r) => (
                            <option key={r} value={r}>
                              {ROLE_LABELS[r] ?? r}
                            </option>
                          ))}
                        </select>
                      )}
                    </TableCell>
                    <TableCell>
                      <label className="inline-flex items-center gap-2 text-sm">
                        <input
                          type="checkbox"
                          checked={user.tabletMode === true}
                          disabled={tabletMutation.isPending}
                          onChange={(e) =>
                            tabletMutation.mutate({
                              id: user.id,
                              tabletMode: e.target.checked,
                            })
                          }
                        />
                        <span className="text-fg-secondary">Wall display</span>
                      </label>
                    </TableCell>
                    <TableCell className="text-right">
                      {isMe ? (
                        <span className="text-xs text-fg-muted">You</span>
                      ) : (
                        <Button
                          variant="ghost"
                          size="sm"
                          className="text-danger"
                          onClick={() => deactivateMutation.mutate(user.id)}
                        >
                          Deactivate
                        </Button>
                      )}
                    </TableCell>
                  </TableRow>
                );
              })}
            </TableBody>
          </Table>
        </div>
      )}

      <InviteDialog
        open={inviteOpen}
        onOpenChange={setInviteOpen}
        onSubmit={(input) => inviteMutation.mutate(input)}
        pending={inviteMutation.isPending}
        error={inviteMutation.error}
      />
    </>
  );
}

interface InviteDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  onSubmit: (input: { name: string; email: string; role: string }) => void;
  pending: boolean;
  error: unknown;
}

function InviteDialog({ open, onOpenChange, onSubmit, pending, error }: InviteDialogProps) {
  const [name, setName] = useState('');
  const [email, setEmail] = useState('');
  const [role, setRole] = useState('building_manager');

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>Invite a teammate</DialogTitle>
          <DialogDescription>
            They'll receive an email with a one-time activation link.
          </DialogDescription>
        </DialogHeader>
        <form
          className="space-y-4"
          onSubmit={(e) => {
            e.preventDefault();
            onSubmit({ name, email, role });
          }}
          noValidate
        >
          <div className="space-y-1.5">
            <Label htmlFor="invite-name">Full name</Label>
            <Input
              id="invite-name"
              required
              value={name}
              onChange={(e) => setName(e.target.value)}
            />
          </div>
          <div className="space-y-1.5">
            <Label htmlFor="invite-email">Email</Label>
            <Input
              id="invite-email"
              type="email"
              required
              value={email}
              onChange={(e) => setEmail(e.target.value)}
            />
          </div>
          <div className="space-y-1.5">
            <Label htmlFor="invite-role">Role</Label>
            <select
              id="invite-role"
              value={role}
              onChange={(e) => setRole(e.target.value)}
              className="h-9 w-full rounded border border-border bg-surface px-3 text-sm"
            >
              {ROLES.map((r) => (
                <option key={r} value={r}>
                  {ROLE_LABELS[r] ?? r}
                </option>
              ))}
            </select>
          </div>
          {error ? (
            <Alert variant="danger">
              <AlertDescription>
                {error instanceof ApiError ? error.message : 'Could not send invitation.'}
              </AlertDescription>
            </Alert>
          ) : null}
          <DialogFooter>
            <Button type="button" variant="ghost" onClick={() => onOpenChange(false)}>
              Cancel
            </Button>
            <Button type="submit" disabled={pending}>
              {pending ? 'Sending…' : 'Send invitation'}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}
